'use client'
import Image from 'next/image';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { noto_sans, noto_serif, RASHIES } from '@/globalConstants';
import { Rashi } from '@/globalTypes';
import PageBanner from '../PageBanner';

import BreadCrumb from '@/components/BreadCrumb';

const BASE_CLASS = 'rashi';

// `summaries` (today's prediction per sign, fetched on the server) puts real
// horoscope text on this page, not just the twelve sign names.
// `heading` names the day ("Today's Rashifal – 15 October 2026"), set on the server.
// Each sign also links to its other periods; weekly pages earn the best CTR.
const OTHER_PERIODS = [
  { key: 'tomorrow', label: 'Tomorrow' },
  { key: 'this-week', label: 'This week' },
  { key: 'next-week', label: 'Next week' },
];

const Rashies = ({ summaries = {}, heading = 'Rashifal' }: { summaries?: Record<string, string>; heading?: string }) => {
  const pathname = usePathname();

  return (
    <div className='w-full'>
      <PageBanner
        breadcrumb={<BreadCrumb pathname={pathname} />}
        title={heading}
        subtitle={'Select a rashi to see your rashifal'}
        align="center"
      />
      <div className='mb-20'></div>
      <div className={`${BASE_CLASS} py-10 pt-4 flex justify-center`}>
        <div className={`${BASE_CLASS}__container grid gap-4 md:gap-6 w-[95%] md:w-[90%] 2xl:w-[70%]`}>
          <div className={`${BASE_CLASS}__deity-list-container grid gap-5 md:gap-6`}>

            {/* Rashi Cards */}
            <div className={`${BASE_CLASS}__rashifal grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-6`}>
              {RASHIES.map((rashi: Rashi) => (
                <div key={rashi.english_name} className="flex flex-col gap-2 w-full max-w-[230px] md:max-w-none">
                <Link
                  href={`/rashifal/${rashi.english_name.toLowerCase().trim()}/today`}
                  className="group"
                >
                  <div
                    className={`group ${BASE_CLASS}__rashi-card relative flex px-3 py-5 md:px-4 md:py-6 rounded-lg cursor-pointer transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-[#8B5CF6] bg-white hover:bg-[#FFEEBB] border border-[#EDE4DD] shadow-sm hover:shadow-md w-full max-w-[230px] md:max-w-none`}
                  >
                    {/* Left side: Icon and names in flex-col */}
                    <div className="flex flex-col">
                      {/* Icon at top left */}
                      <div className="w-16 h-16 flex items-center justify-center mb-3">
                        <Image
                          src={rashi.image_url}
                          alt={`${rashi.vedic_name} - ${rashi.english_name}`}
                          width={80}
                          height={80}
                          className="rashi-image transition-transform duration-300 group-hover:scale-110"
                        />
                      </div>

                      {/* Names below icon */}
                      <div className="flex flex-col mt-4 sm:mt-4 md:mt-0">
                        <h2 className={`${BASE_CLASS}_vedic-name font-semibold ${noto_sans.className} text-base md:text-lg text-[#0F4B4A] mb-1`}>
                          {rashi.vedic_name}
                        </h2>
                        <p className={`${BASE_CLASS}_english-name ${noto_sans.className} font-medium text-sm text-[#7B7676] group-hover:text-[#3C3838]`}>
                          {rashi.english_name}
                        </p>
                        {summaries[rashi.english_name.toLowerCase().trim()] && (
                          <p className={`${BASE_CLASS}_summary ${noto_sans.className} mt-2 pr-8 text-xs md:text-sm text-[#5C5A58] line-clamp-3`}>
                            {summaries[rashi.english_name.toLowerCase().trim()]}
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Arrow at bottom right */}
                    <div className="absolute right-4 bottom-8">
                      <Image
                        src="/arrow-orange.svg"
                        alt="Go to Rashifal"
                        width={24}
                        height={24}
                      />
                    </div>
                  </div>
                </Link>
                <nav aria-label={`${rashi.english_name} rashifal for other periods`} className={`${noto_sans.className} flex flex-wrap gap-x-2 gap-y-1 px-1 text-xs md:text-sm`}>
                  {OTHER_PERIODS.map(({ key, label }, index) => (
                    <span key={key} className="text-[#8F8B8B]">
                      {index > 0 && '· '}
                      <Link href={`/rashifal/${rashi.english_name.toLowerCase().trim()}/${key}`} className="text-[#EE9322] no-underline hover:underline">{label}</Link>
                    </span>
                  ))}
                </nav>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Rashies;
